/**
 * The drawing surface: Geoman's configuration in one place, away from the map's wiring.
 *
 * Ported from the main product's `GeomanController`
 * (`frontend/src/components/ui/GeomanController.tsx`), with its central idea intact and its
 * seams re-cut:
 *
 *   - EDITING IS ALWAYS ON. `addControls({ editMode: false })` there does not mean "cannot
 *     edit", it means "there is no edit MODE": `pm.enable()` is called on every polygon, so
 *     the vertices are there the moment the ring closes and "click it first" is a step that
 *     exists to be skipped. That is kept;
 *   - GEOMAN'S OWN TOOLBAR IS NOT ADDED. The source mounts `addControls`; we already have a
 *     toolbar of our own (components/DrawTools.tsx) with our words, our icons and our undo
 *     buttons, and two toolbars over one map is not a port, it is a duplicate;
 *   - VERTEX EVENTS GO INTO THE HISTORY. The source listens to `pm:markerdragend` only, so
 *     deleting a vertex with a right-click or inserting one from a midpoint left no point in
 *     the undo stack — Ctrl+Z then rolled back something else. `pm:vertexadded` and
 *     `pm:vertexremoved` are listened to here. This is a deliberate difference from the
 *     source, not an accident;
 *   - LIVE HECTARES. Geoman announces a shape only when the ring closes (`pm:create`), and
 *     the contract is an area readout that is live while the ring is still being drawn
 *     (docs/LEAFLET_TARGET_CONTRACT.md §4). So the working layer handed out by `pm:drawstart`
 *     is listened to as well and a provisional polygon is published on every vertex.
 *
 * `allowSelfIntersection` is left at Geoman's default (allowed). A bow tie must be drawable
 * and then REFUSED IN WORDS by lib/validateGeometry.ts — silently preventing the gesture
 * teaches nothing and hides the reason the area would have been wrong.
 */

import * as L from "leaflet";
import "@geoman-io/leaflet-geoman-free";

import type { GeoJsonPolygon } from "../api/types";
import { combinePolygons } from "../lib/geo";
import type { DrawMode } from "../components/DrawTools";
import type { MapPalette } from "./basemap";
import { createHistory } from "./history";
import { ringToLatLngs, toLonLat } from "./latlng";

export interface DrawHandlers {
  /** Every change, mid-ring included: the hectares readout must be live. */
  onGeometry: (geometry: GeoJsonPolygon | null) => void;
  onHistory: (canUndo: boolean, canRedo: boolean) => void;
}

export interface DrawController {
  undo: () => void;
  redo: () => void;
  setMode: (mode: DrawMode) => void;
  clear: () => void;
  stop: () => void;
}

/** Marks the polygons the operator drew, so the saved-field layers are never collected. */
const DRAFT = "__kopateliDraft";

type DraftPolygon = L.Polygon & { [DRAFT]?: true };

interface PmLayer {
  pm?: { enable: (options?: unknown) => void; disable: () => void };
}

interface PolygonDrawHandler {
  _removeLastVertex?: () => void;
}

const isDraft = (layer: L.Layer): layer is DraftPolygon => (layer as DraftPolygon)[DRAFT] === true;

const geometryOf = (layer: L.Polygon): GeoJsonPolygon | null => {
  const feature = layer.toGeoJSON();
  const geometry = feature.type === "Feature" ? feature.geometry : null;
  if (!geometry) return null;
  return geometry.type === "Polygon" || geometry.type === "MultiPolygon"
    ? (geometry as GeoJsonPolygon)
    : null;
};

/** The ring being drawn right now, closed, or null while it is still a line. */
const workingRing = (layer: L.Polygon): GeoJsonPolygon | null => {
  const raw = layer.getLatLngs() as L.LatLng[] | L.LatLng[][];
  const points = (Array.isArray(raw[0]) ? raw[0] : raw) as L.LatLng[];
  if (!points || points.length < 3) return null;
  const ring = points.map(toLonLat);
  ring.push(ring[0] as [number, number]);
  return { type: "Polygon", coordinates: [ring] };
};

export const attachDrawing = (
  map: L.Map,
  palette: MapPalette,
  handlers: DrawHandlers,
): DrawController => {
  const history = createHistory<GeoJsonPolygon | null>(null);
  let mode: DrawMode = "idle";
  let working: L.Polygon | null = null;
  let workingVertices = 0;
  let restoring = false;

  const style: L.PathOptions = {
    color: palette.selectedLine,
    weight: 2,
    fillColor: palette.selectedFill,
    fillOpacity: 0.2,
  };

  map.pm.setGlobalOptions({
    snappable: true,
    snapDistance: 15,
    tooltips: false,
    pathOptions: style,
    templineStyle: { color: palette.selectedLine, weight: 2 },
    hintlineStyle: { color: palette.selectedLine, weight: 2, dashArray: "5,5" },
  });

  const drafts = (): DraftPolygon[] => {
    const found: DraftPolygon[] = [];
    map.eachLayer((layer) => {
      if (isDraft(layer)) found.push(layer);
    });
    return found;
  };

  const finished = (): GeoJsonPolygon[] =>
    drafts()
      .map(geometryOf)
      .filter((geometry): geometry is GeoJsonPolygon => geometry !== null);

  /** What the toolbar sees. `extra` is the unfinished ring, when there is one. */
  const publish = (extra: GeoJsonPolygon | null = null): GeoJsonPolygon | null => {
    const geometry = combinePolygons(extra ? [...finished(), extra] : finished());
    handlers.onGeometry(geometry);
    return geometry;
  };

  // Undo is available while a ring is open too: taking back the last vertex is the commonest
  // correction there is, and it happens before any snapshot exists.
  const publishHistory = (): void =>
    handlers.onHistory(history.canUndo() || workingVertices > 0, history.canRedo());

  const remember = (): void => {
    if (restoring) return;
    history.push(publish());
    publishHistory();
  };

  const editable = (): boolean => mode !== "idle";

  const enableEdit = (layer: DraftPolygon): void => {
    const pm = (layer as PmLayer).pm;
    if (!pm) return;
    if (editable()) pm.enable({ allowSelfIntersection: true });
    else pm.disable();
  };

  const applyEditability = (): void => drafts().forEach(enableEdit);

  const adopt = (layer: L.Polygon): void => {
    (layer as DraftPolygon)[DRAFT] = true;
    // Removed first: undo/redo re-adds the same handlers otherwise, and a dozen restores
    // later one vertex drag pushes a dozen snapshots.
    layer.off("pm:markerdragend", remember);
    layer.off("pm:vertexadded", remember);
    layer.off("pm:vertexremoved", remember);
    layer.on("pm:markerdragend", remember);
    layer.on("pm:vertexadded", remember);
    layer.on("pm:vertexremoved", remember);
    enableEdit(layer as DraftPolygon);
  };

  const wipe = (): void => {
    for (const layer of drafts()) map.removeLayer(layer);
  };

  /**
   * Put a snapshot back on the map. Synchronous on purpose: the source clears its
   * `isRestoring` flag one animation frame later and bets that Leaflet has dispatched every
   * `layeradd` by then. Nothing here waits for a frame, so there is no race to lose.
   */
  const restore = (geometry: GeoJsonPolygon | null): void => {
    restoring = true;
    wipe();
    if (geometry) {
      // One `L.Polygon` per part rather than an `L.GeoJSON` group: the group would stay on
      // the map after its children are wiped, and it would hand them back on the next
      // operation. Parts are separate layers here for the same reason they were separate
      // when they were drawn.
      const parts =
        geometry.type === "Polygon"
          ? [geometry.coordinates as number[][][]]
          : (geometry.coordinates as number[][][][]);
      for (const part of parts) {
        adopt(L.polygon(part.map(ringToLatLngs), style).addTo(map));
      }
    }
    restoring = false;
    publish();
    publishHistory();
  };

  const onCreate = (event: unknown): void => {
    const layer = (event as { layer?: L.Layer }).layer;
    if (layer instanceof L.Polygon) adopt(layer);
    remember();
  };

  const onWorkingVertex = (): void => {
    if (!working) return;
    workingVertices = (working.getLatLngs() as unknown[]).length;
    publish(workingRing(working));
    publishHistory();
  };

  const onDrawStart = (event: unknown): void => {
    const layer = (event as { workingLayer?: L.Polygon }).workingLayer;
    working = layer ?? null;
    workingVertices = 0;
    working?.on("pm:vertexadded", onWorkingVertex);
    working?.on("pm:vertexremoved", onWorkingVertex);
  };

  const onDrawEnd = (): void => {
    working?.off("pm:vertexadded", onWorkingVertex);
    working?.off("pm:vertexremoved", onWorkingVertex);
    working = null;
    workingVertices = 0;
    publishHistory();
  };

  map.on("pm:create", onCreate);
  map.on("pm:remove", remember);
  map.on("pm:drawstart", onDrawStart);
  map.on("pm:drawend", onDrawEnd);

  const removeLastVertex = (): boolean => {
    if (workingVertices === 0) return false;
    const polygon = (map.pm.Draw as unknown as { Polygon?: PolygonDrawHandler }).Polygon;
    if (typeof polygon?._removeLastVertex !== "function") return false;
    polygon._removeLastVertex();
    return true;
  };

  return {
    // Two levels, the same pair Terra Draw gave for free: the ring being drawn right now
    // first (take back the last vertex), finished shapes second.
    undo: () => {
      if (removeLastVertex()) return;
      if (!history.canUndo()) return;
      restore(history.undo());
    },
    redo: () => {
      if (!history.canRedo()) return;
      restore(history.redo());
    },
    setMode: (next) => {
      mode = next;
      if (next === "draw") map.pm.enableDraw("Polygon");
      else map.pm.disableDraw();
      applyEditability();
    },
    clear: () => {
      map.pm.disableDraw();
      wipe();
      // Otherwise undo resurrects a contour the operator has just wiped.
      history.reset(null);
      workingVertices = 0;
      handlers.onGeometry(null);
      handlers.onHistory(false, false);
      if (mode === "draw") map.pm.enableDraw("Polygon");
    },
    stop: () => {
      map.off("pm:create", onCreate);
      map.off("pm:remove", remember);
      map.off("pm:drawstart", onDrawStart);
      map.off("pm:drawend", onDrawEnd);
      onDrawEnd();
      map.pm.disableDraw();
    },
  };
};
